import { convertProductToLineItem, createLineItemsFromProducts } from 'commerce-products-lib/utils/lineItems';
import { CREATE_DATE, LINE_ITEM_CURRENCY_CODE, ORDER, PRODUCT_ID, SKU } from 'customer-data-objects/lineItem/PropertyNames';
import ProductRecord from 'customer-data-objects/product/ProductRecord';
import { CREATED_BY } from 'customer-data-objects/task/TaskPropertyNames';
import isEqual from 'hs-lodash/isEqual';
import { ALLOW_OPEN_AMOUNT, OPTIONAL } from 'products-ui-components/constants/supplementalProperties';
import { isTrue } from 'products-ui-components/utils/isTrue';
import toNumber from 'products-ui-components/utils/toNumber';
import isEmpty from 'transmute/isEmpty';
import uniqueId from 'transmute/uniqueId';
import { getProperty } from './objectAccessors';
import { hasBadAmount } from './properties/integrations';
import { hasNegativePrice } from './properties/price';
import { getId, getLineItems, getProduct } from './subject';
export function getModifiedProperties(oldLineItem, newLineItem) {
  if (!oldLineItem || isEmpty(oldLineItem) || isEmpty(newLineItem)) {
    return new Map();
  }
  return new Map(Object.entries(newLineItem.properties).filter(([key, property]) => {
    const oldProperty = oldLineItem.properties[key];

    // If the old property was null it has been added to newLineItem
    if (oldProperty == null) {
      return true;
    }

    // Existing property was updated
    return !isEqual(oldProperty, property);
  }));
}
export function hasModifiedLineItems(editedLineItems) {
  if (!editedLineItems) {
    return false;
  }
  const {
    lineItemsToUpdate,
    lineItemsToAdd,
    lineItemsToRemove
  } = editedLineItems;
  return lineItemsToUpdate.size > 0 || lineItemsToAdd.size > 0 || lineItemsToRemove.size > 0;
}

/**
 * Extracts IDs from line items that are not newly created
 *
 * @param lineItems - Array of LineItem objects to process
 * @returns Array of IDs from existing (non-new) line items
 */
export function getUpdatedLineItemIds(lineItems) {
  return lineItems.filter(lineItem => !lineItem.isNew).map(lineItem => getId(lineItem));
}

/**
 * Given line items, filter out those who do not have the given effectiveCurrencyCode.
 * Extra logic to check if validValueOptions contains the product currency property
 * the line item was created from. If so, construct new line item with effectiveCurrencyCode.
 */
export const filterValidLineItemsWithCurrency = async (lineItems, effectiveCurrencyCode, hasMulticurrencyActive, validValueOptions = []) => {
  const filteredLineItems = [];
  const lineItemsToCreate = [];
  for (const lineItem of lineItems) {
    if (!hasMulticurrencyActive || getProperty(lineItem, LINE_ITEM_CURRENCY_CODE) === effectiveCurrencyCode) {
      filteredLineItems.push(lineItem);
    }

    // If we have a matching option that contains the effectiveCurrencyCode,
    // we can construct a new line item with the effectiveCurrencyCode and price
    const matchingOption = validValueOptions.find(option => option.record && option.record.getIn(['referencedObject', 'objectId']) === toNumber(getProperty(lineItem, PRODUCT_ID)));
    if (matchingOption !== undefined && matchingOption.record) {
      lineItemsToCreate.push(matchingOption.record.id);
    }
  }
  try {
    const createdLineItems = await createLineItemsFromProducts(lineItemsToCreate.map(id => ({
      objectId: toNumber(id),
      // TODO: stop passing properties when createLineItemsFromProducts accepts a list of Product Ids instead
      properties: {}
    })), hasMulticurrencyActive ? effectiveCurrencyCode : undefined);
    if (Array.isArray(createdLineItems) && createdLineItems.length > 0) {
      filteredLineItems.push(createdLineItems[0]);
    }
  } catch (error) {
    console.error(error);
    return filteredLineItems;
  }
  return filteredLineItems;
};

/**
 * Given line items, filter out those who do not have the given effectiveCurrencyCode.
 * Extra logic to check if validValueOptions contains the product currency property
 * the line item was created from. If so, construct new line item with effectiveCurrencyCode.
 */
export function filterValidLineItemsWithCurrencyLegacy(lineItems, defaultCurrencyCode, effectiveCurrencyCode, hasMulticurrencyActive, multicurrencies, validValueOptions = []) {
  return lineItems.reduce((acc, lineItem) => {
    if (!hasMulticurrencyActive || getProperty(lineItem, LINE_ITEM_CURRENCY_CODE) === effectiveCurrencyCode) {
      acc.push(lineItem);
      return acc;
    }

    // If we have a matching option that contains the effectiveCurrencyCode,
    // we can construct a new line item with the effectiveCurrencyCode and price
    const matchingOption = validValueOptions.find(option => option.record && option.record.getIn(['referencedObject', 'objectId']) === toNumber(getProperty(lineItem, PRODUCT_ID)));
    if (matchingOption !== undefined && matchingOption.record) {
      acc.push(convertProductToLineItem(getProduct(ProductRecord(matchingOption.record.referencedObject)), hasMulticurrencyActive ? effectiveCurrencyCode : null, multicurrencies, defaultCurrencyCode));
    }
    return acc;
  }, []);
}
export function getTemporaryLineItemId() {
  return Number(uniqueId('-'));
}
const NON_CLONABLE_PRODUCT_LI_PROPERTIES = new Set([ORDER, CREATE_DATE, CREATED_BY]);
const NON_CLONABLE_CUSTOM_LI_PROPERTIES = new Set([...NON_CLONABLE_PRODUCT_LI_PROPERTIES, SKU]);
export function getLineItemPropertiesForClone(lineItem) {
  const {
    properties
  } = lineItem;
  const productId = properties[PRODUCT_ID];
  const isLineItemFromProduct = productId != null && productId.value != null;
  const propertiesToSkip = isLineItemFromProduct ?
  // Clone SKU from product line items
  NON_CLONABLE_PRODUCT_LI_PROPERTIES :
  // Skip SKU for custom line items
  NON_CLONABLE_CUSTOM_LI_PROPERTIES;
  return Object.fromEntries(Object.entries(properties).filter(([propertyName]) => !propertiesToSkip.has(propertyName)));
}
export function isOptionalLineItem(lineItem) {
  return isTrue(getProperty(lineItem, OPTIONAL));
}
export function areAllLineItemsOptional(lineItems) {
  return getLineItems(lineItems).every(lineItem => isOptionalLineItem(lineItem));
}
export function isOpenAmountLineItem(lineItem) {
  return isTrue(getProperty(lineItem, ALLOW_OPEN_AMOUNT));
}
export function areAllLineItemsOpenAmount(lineItems) {
  return getLineItems(lineItems).every(lineItem => isOpenAmountLineItem(lineItem));
}
export function isLineItemReadOnly(lineItem) {
  return hasBadAmount(lineItem) || hasNegativePrice(lineItem);
}